/**
 * DecantsLab Search Loader
 * Archivo: assets/dl-search-loader.js
 *
 * Objetivo:
 * - No cargar el buscador premium completo en todas las páginas.
 * - Cargar luxury-search-modal.liquid solo cuando el usuario abre búsqueda.
 * - Mantener quick add usando window.DecantsCart.addForm().
 */

(function () {
  'use strict';

  if (window.DecantsSearchLoader && window.DecantsSearchLoader.__initialized) return;

  const ROOT =
    window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';

  const ROOT_URL = ROOT.endsWith('/') ? ROOT : ROOT + '/';

  const SEARCH_URL =
    window.routes && window.routes.search_url
      ? window.routes.search_url
      : ROOT_URL + 'search';

  const SECTION_ID = 'luxury-search-modal';
  const MODAL_ID = 'DecantsSearchModal';
  const MOUNT_ID = 'DecantsSearchMount';

  const state = {
    loadPromise: null,
    initialized: false,
    defaultGridContent: '',
    debounceTimer: null,
    searchController: null,
    searchRequestId: 0,
  };

  function log() {
    // Cambiar a true si quieres debug temporal.
    const debug = false;
    if (debug) console.log('[DecantsSearch]', ...arguments);
  }

  function buildUrl(baseUrl, params) {
    const separator = baseUrl.includes('?') ? '&' : '?';
    return baseUrl + separator + new URLSearchParams(params).toString();
  }

  function getModal() {
    return document.getElementById(MODAL_ID);
  }

  function getMount() {
    let mount = document.getElementById(MOUNT_ID);

    if (!mount) {
      mount = document.createElement('div');
      mount.id = MOUNT_ID;
      document.body.appendChild(mount);
    }

    return mount;
  }

  function lockScroll() {
    document.body.classList.add('decants-lock-scroll');
    document.documentElement.classList.add('decants-lock-scroll');
  }

  function unlockScroll() {
    document.body.classList.remove('decants-lock-scroll');
    document.documentElement.classList.remove('decants-lock-scroll');
  }

  async function fetchModalHTML() {
    /**
     * Intentamos dos rutas para hacerlo más robusto:
     * 1. Home con section_id.
     * 2. Search con section_id.
     */
    const candidateUrls = [
      buildUrl(ROOT_URL, { section_id: SECTION_ID }),
      buildUrl(SEARCH_URL, { section_id: SECTION_ID }),
    ];

    let lastError = null;

    for (const url of candidateUrls) {
      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (!response.ok) {
          throw new Error('No se pudo cargar el buscador desde ' + url);
        }

        const html = await response.text();

        if (html && html.includes(MODAL_ID)) {
          return html;
        }
      } catch (error) {
        lastError = error;
        log(error);
      }
    }

    throw lastError || new Error('No se encontró el HTML del search modal.');
  }

  async function ensureModalLoaded() {
    if (getModal()) {
      initModal();
      return getModal();
    }

    if (state.loadPromise) {
      await state.loadPromise;
      initModal();
      return getModal();
    }

    state.loadPromise = fetchModalHTML()
      .then(function (html) {
        const mount = getMount();

        mount.innerHTML = html;

        /**
         * Importante:
         * Los scripts insertados con innerHTML no se ejecutan de forma fiable.
         * Además, ahora toda la lógica vive en este loader.
         */
        mount.querySelectorAll('script').forEach(function (script) {
          script.remove();
        });

        initModal();

        return getModal();
      })
      .catch(function (error) {
        console.error('[DecantsSearch] Error cargando modal:', error);
        throw error;
      });

    return state.loadPromise;
  }

  function initModal() {
    const modal = getModal();
    if (!modal || modal.dataset.dlSearchInitialized === 'true') return;

    modal.dataset.dlSearchInitialized = 'true';

    const searchInput = modal.querySelector('#DecantsSearchInput');
    const resultsContainer = modal.querySelector('#DecantsSearchResults');
    const spinner = modal.querySelector('#SearchSpinner');
    const viewAllLink = modal.querySelector('#ViewAllLink');
    const searchFooter = modal.querySelector('#SearchFooter');

    if (resultsContainer) {
      state.defaultGridContent = resultsContainer.innerHTML;
      // Delegación: cambio de tamaño en cualquier <select> de variante.
      resultsContainer.addEventListener('change', onVariantChange);
    }

    modal.addEventListener('click', function (event) {
      if (event.target === modal) {
        closeSearchModal();
      }
    });

    modal.querySelectorAll('.decants-close-btn').forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        closeSearchModal();
      });
    });

    if (searchInput && resultsContainer) {
      searchInput.addEventListener('input', function (event) {
        const term = event.target.value.trim();

        if (viewAllLink) {
          viewAllLink.href = buildUrl(SEARCH_URL, { q: term, type: 'product' });
        }

        window.clearTimeout(state.debounceTimer);

        if (term.length === 0) {
          resultsContainer.innerHTML =
            state.defaultGridContent || '<div class="empty-state">Empieza a escribir...</div>';

          if (spinner) spinner.style.display = 'none';
          if (searchFooter) searchFooter.style.display = 'none';

          return;
        }

        if (searchFooter) searchFooter.style.display = 'block';
        if (spinner) spinner.style.display = 'block';

        state.debounceTimer = window.setTimeout(function () {
          performSearch(term);
        }, 300);
      });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && modal.classList.contains('active')) {
        closeSearchModal();
      }
    });
  }

  /* ======================================================================
   *  MOTOR DE BÚSQUEDA INTELIGENTE (re-ranking del lado del cliente)
   *  Prioridad de campo: título > proveedor > tipo/colección > tags > desc.
   *  Cercanía dentro del campo: exacto > empieza-con > palabra > substring.
   * ==================================================================== */
  const RANK = {
    // Peso base por campo (mayor = más importante). Ajustar a gusto.
    FIELD_WEIGHTS: {
      title: 100, // Título — máxima prioridad
      vendor: 70, // Proveedor / marca
      product_type: 45, // Tipo (actúa como "colección" lógica)
      tags: 35, // Tags (colecciones, familias olfativas, etc.)
      description: 15, // Descripción — menor prioridad
    },
    // Multiplicadores de cercanía dentro de cada campo.
    MATCH_EXACT: 2.0,
    MATCH_STARTS_WITH: 1.5,
    MATCH_WORD: 1.2,
    MATCH_SUBSTRING: 1.0,
    // Config general.
    MAX_RESULTS: 12,
    CANDIDATE_LIMIT: 10,
    HIDDEN_TAGS: ['regalo', 'guia_upsell'],
    AVAILABLE_BONUS: 40, // empuja disponibles por sobre agotados de relevancia similar
  };

  // Normaliza: minúsculas + sin acentos + espacios colapsados.
  function normText(str) {
    if (!str) return '';
    return String(str)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function moneyFormat(cents) {
    // Shopify guarda TODOS los precios en centavos (x100), incluso en CLP
    // (moneda de cero decimales). Lo correcto es usar el formateador nativo
    // de la tienda, que respeta money_format y divide /100 internamente.
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const fmt =
        (window.Shopify && window.Shopify.money_format) ||
        (window.theme && window.theme.moneyFormat) ||
        window.moneyFormat ||
        '${{amount_no_decimals}}';
      try {
        return window.Shopify.formatMoney(cents, fmt);
      } catch (e) {
        /* cae al fallback */
      }
    }
    // Fallback CLP (cero decimales): centavos -> valor real dividiendo /100,
    // sin decimales y con punto de miles. 9800000¢ -> $98.000
    const val = Math.round(Number(cents) / 100);
    return '$' + val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  function escHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // Multiplicador de cercanía de un término dentro de un campo (0 = sin match).
  function proximityScore(fieldValue, term) {
    const f = normText(fieldValue);
    if (!f || !term) return 0;
    if (f === term) return RANK.MATCH_EXACT;
    if (f.indexOf(term) === 0) return RANK.MATCH_STARTS_WITH;
    const safe = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (new RegExp('(^|\\s)' + safe + '(\\s|$)').test(f)) return RANK.MATCH_WORD;
    if (f.indexOf(term) !== -1) return RANK.MATCH_SUBSTRING;
    return 0;
  }

  // Puntúa un producto contra todos los términos (AND lógico entre términos).
  // Devuelve -1 si algún término no aparece en ningún campo.
  function scoreProduct(product, terms) {
    const fields = {
      title: product.title || '',
      vendor: product.vendor || '',
      product_type: product.product_type || '',
      tags: (product.tags || []).join(' '),
      description: product.body || product.description || '',
    };

    let total = 0;
    for (let i = 0; i < terms.length; i++) {
      const term = terms[i];
      let bestForTerm = 0;
      for (const key in RANK.FIELD_WEIGHTS) {
        const prox = proximityScore(fields[key], term);
        if (prox > 0) {
          const s = RANK.FIELD_WEIGHTS[key] * prox;
          if (s > bestForTerm) bestForTerm = s;
        }
      }
      if (bestForTerm === 0) return -1;
      total += bestForTerm;
    }
    if (product.available) total += RANK.AVAILABLE_BONUS;
    return total;
  }

  // Trae candidatos desde suggest.json (frase completa + cada término suelto).
  async function fetchCandidates(rawQuery, signal) {
    const terms = normText(rawQuery).split(' ').filter(Boolean);
    if (!terms.length) return [];

    const suggestBase = buildUrl(ROOT_URL + 'search/suggest.json', {
      'resources[type]': 'product',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,vendor,tag,body',
      'resources[limit]': String(RANK.CANDIDATE_LIMIT),
    });

    const queries = [rawQuery].concat(terms.length > 1 ? terms : []);
    const requests = queries.map(function (q) {
      const url = suggestBase + '&q=' + encodeURIComponent(q);
      return fetch(url, { signal, headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .catch(function () {
          return null;
        });
    });

    const responses = await Promise.all(requests);
    const seen = {};
    const pool = [];
    responses.forEach(function (data) {
      const products =
        data && data.resources && data.resources.results && data.resources.results.products
          ? data.resources.results.products
          : [];
      products.forEach(function (p) {
        if (!p || seen[p.id]) return;
        seen[p.id] = true;
        pool.push(p);
      });
    });
    return pool;
  }

  // Construye el HTML de un resultado (mismo markup que el .liquid).
  // Extrae el handle desde la url del producto (…/products/handle?…).
  function handleFromUrl(url) {
    if (!url) return '';
    const m = String(url).match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  // Pide /products/{handle}.js para obtener variantes+precios reales.
  // Devuelve null si falla (usaremos los datos parciales de suggest).
  async function hydrateProduct(product, signal) {
    const handle = product.handle || handleFromUrl(product.url);
    if (!handle) return null;
    try {
      const res = await fetch(ROOT_URL + 'products/' + handle + '.js', {
        signal,
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  // Construye un id único por producto para enlazar el <select> con su form.
  function pidOf(p) {
    return 'dl-' + (p.id || Math.random().toString(36).slice(2));
  }

  function renderResultItem(p) {
    const img =
      p.featured_image && p.featured_image.url ? p.featured_image.url : p.image || '';

    // Preferimos variantes completas (de product.js). Fallback: lo que haya.
    const variants = Array.isArray(p.variants) ? p.variants : [];
    const firstAvail = variants.find(function (v) {
      return v.available;
    });
    const selected = firstAvail || variants[0] || null;

    const productAvailable =
      typeof p.available === 'boolean'
        ? p.available
        : variants.some(function (v) {
            return v.available;
          });

    const selPrice = selected && selected.price != null ? selected.price : p.price || 0;
    const selCompare =
      (selected && selected.compare_at_price) || p.compare_at_price_min || 0;
    const uid = pidOf(p);

    const imgHtml = img
      ? '<img src="' +
        escHtml(img) +
        '" alt="' +
        escHtml(p.title) +
        '" class="dp-image" width="80" height="80" loading="lazy">'
      : '<div class="dp-image-placeholder"></div>';

    const compareHtml =
      selCompare > selPrice
        ? '<span class="dp-compare-price">' + moneyFormat(selCompare) + '</span>'
        : '';

    // Selector de variante: solo si hay más de una.
    let selectHtml = '';
    if (variants.length > 1) {
      const opts = variants
        .map(function (v) {
          return (
            '<option value="' +
            escHtml(v.id) +
            '" data-price="' +
            escHtml(v.price != null ? v.price : '') +
            '" data-compare="' +
            escHtml(v.compare_at_price != null ? v.compare_at_price : '') +
            '" ' +
            (selected && v.id === selected.id ? 'selected' : '') +
            (v.available ? '' : ' disabled') +
            '>' +
            escHtml(v.title) +
            (v.available ? '' : ' (agotado)') +
            '</option>'
          );
        })
        .join('');
      selectHtml =
        '<select class="dp-variant-select" data-uid="' +
        uid +
        '" aria-label="Elegir tamaño">' +
        opts +
        '</select>';
    }

    const btnLabel = productAvailable ? 'AÑADIR' : 'AGOTADO';
    const selectedId = selected ? selected.id : '';

    return (
      '<div class="decants-product-item" data-uid="' +
      uid +
      '">' +
      '<a href="' +
      escHtml(p.url) +
      '" class="dp-image-link">' +
      imgHtml +
      '</a>' +
      '<div class="dp-info">' +
      '<a href="' +
      escHtml(p.url) +
      '" class="dp-title-link"><p class="dp-title">' +
      escHtml(p.title) +
      '</p></a>' +
      '<span class="dp-price" data-uid="' +
      uid +
      '">' +
      compareHtml +
      moneyFormat(selPrice) +
      '</span>' +
      '</div>' +
      '<div class="dp-actions">' +
      selectHtml +
      '<form method="post" action="' +
      ROOT_URL +
      'cart/add" class="dp-form-add" onsubmit="submitQuickAdd(event)" enctype="multipart/form-data">' +
      '<input type="hidden" name="id" data-uid="' +
      uid +
      '" value="' +
      escHtml(selectedId) +
      '">' +
      '<button type="submit" class="dp-add-btn" ' +
      (productAvailable ? '' : 'disabled') +
      '>' +
      btnLabel +
      '</button>' +
      '</form>' +
      '</div>' +
      '</div>'
    );
  }

  // Al cambiar el <select>, actualiza el hidden id, el precio y el botón.
  function onVariantChange(event) {
    const select = event.target;
    if (!select.classList || !select.classList.contains('dp-variant-select')) return;

    const uid = select.getAttribute('data-uid');
    const item = select.closest('.decants-product-item');
    if (!item) return;

    const opt = select.options[select.selectedIndex];
    const newId = select.value;
    const price = opt.getAttribute('data-price');
    const compare = opt.getAttribute('data-compare');
    const isAvailable = !opt.disabled;

    const hidden = item.querySelector('input[name="id"]');
    if (hidden) hidden.value = newId;

    const priceEl = item.querySelector('.dp-price');
    if (priceEl && price !== '' && price != null) {
      let html = '';
      if (compare && Number(compare) > Number(price)) {
        html += '<span class="dp-compare-price">' + moneyFormat(compare) + '</span>';
      }
      html += moneyFormat(price);
      priceEl.innerHTML = html;
    }

    const btn = item.querySelector('.dp-add-btn');
    if (btn) {
      btn.disabled = !isAvailable;
      btn.textContent = isAvailable ? 'AÑADIR' : 'AGOTADO';
    }
  }

  async function performSearch(term) {
    const modal = getModal();
    if (!modal) return;

    const resultsContainer = modal.querySelector('#DecantsSearchResults');
    const spinner = modal.querySelector('#SearchSpinner');

    if (!resultsContainer) return;

    state.searchRequestId += 1;
    const currentRequestId = state.searchRequestId;

    if (state.searchController) {
      state.searchController.abort();
    }

    state.searchController = new AbortController();
    const signal = state.searchController.signal;

    const terms = normText(term).split(' ').filter(Boolean);

    try {
      const pool = await fetchCandidates(term, signal);

      if (currentRequestId !== state.searchRequestId) return;

      // Filtrar tags ocultos (regalo, upsell).
      const filtered = pool.filter(function (p) {
        const tags = (p.tags || []).map(normText);
        return !RANK.HIDDEN_TAGS.some(function (h) {
          return tags.indexOf(h) !== -1;
        });
      });

      // Puntuar y ordenar por relevancia.
      const scored = [];
      filtered.forEach(function (p) {
        const s = scoreProduct(p, terms);
        if (s >= 0) scored.push({ p: p, s: s });
      });
      scored.sort(function (a, b) {
        return b.s - a.s;
      });

      const top = scored.slice(0, RANK.MAX_RESULTS).map(function (x) {
        return x.p;
      });

      if (!top.length) {
        resultsContainer.innerHTML =
          '<div class="empty-state" style="padding:40px; text-align:center;">No se encontraron productos.</div>';
        return;
      }

      // Hidratar cada top con /products/handle.js (variantes + precios reales).
      const hydrated = await Promise.all(
        top.map(function (p) {
          return hydrateProduct(p, signal).then(function (full) {
            if (!full) return p; // fallback a datos parciales de suggest
            // Fusionamos: product.js manda en variants/price/available.
            return {
              id: full.id,
              title: full.title,
              url: p.url || ROOT_URL + 'products/' + full.handle,
              handle: full.handle,
              featured_image: p.featured_image,
              image:
                (full.images && full.images[0]) ||
                (p.featured_image && p.featured_image.url) ||
                p.image ||
                '',
              price: full.price,
              compare_at_price_min: full.compare_at_price || 0,
              available: full.available,
              variants: full.variants || [],
            };
          });
        })
      );

      if (currentRequestId !== state.searchRequestId) return;

      resultsContainer.innerHTML = hydrated
        .map(function (p) {
          return renderResultItem(p);
        })
        .join('');
    } catch (error) {
      if (error.name === 'AbortError') return;

      console.error('[DecantsSearch] Error buscando:', error);

      resultsContainer.innerHTML =
        '<div class="empty-state" style="padding:40px; text-align:center;">Error al buscar.</div>';
    } finally {
      if (spinner && currentRequestId === state.searchRequestId) {
        spinner.style.display = 'none';
      }
    }
  }

  async function openSearchModal() {
    try {
      const modal = await ensureModalLoaded();

      if (!modal) return;

      modal.classList.add('active');
      lockScroll();

      const searchInput = modal.querySelector('#DecantsSearchInput');

      if (searchInput) {
        window.setTimeout(function () {
          searchInput.focus();
        }, 120);
      }
    } catch (error) {
      console.error('[DecantsSearch] No se pudo abrir el buscador:', error);
    }
  }

  function closeSearchModal() {
    const modal = getModal();

    if (modal) {
      modal.classList.remove('active');
    }

    unlockScroll();
  }

  async function submitQuickAdd(event) {
    event.preventDefault();

    const form = event.currentTarget || event.target;
    const button = form.querySelector('button[type="submit"], button');
    const originalHTML = button ? button.innerHTML : '';

    if (!window.DecantsCart || typeof window.DecantsCart.addForm !== 'function') {
      console.warn('[DecantsSearch] DecantsCart no está disponible.');
      return;
    }

    try {
      if (button) {
        button.innerHTML = '...';
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      }

      closeSearchModal();

      await window.DecantsCart.addForm(form, {
        openDrawer: true,
        source: 'luxury-search-modal',
      });

      if (button) {
        button.innerHTML = '¡LISTO!';
      }
    } catch (error) {
      console.error('[DecantsSearch] Error agregando producto:', error);

      if (button) {
        button.innerHTML = 'ERROR';
      }
    } finally {
      if (button) {
        window.setTimeout(function () {
          button.innerHTML = originalHTML;
          button.disabled = false;
          button.removeAttribute('aria-disabled');
        }, 1200);
      }
    }
  }

  /**
   * Delegación para botones de búsqueda.
   * Permite reemplazar onclick por data-dl-search-open.
   */
  document.addEventListener('click', function (event) {
    const trigger = event.target.closest('[data-dl-search-open]');

    if (!trigger) return;

    event.preventDefault();
    openSearchModal();
  });

  /**
   * Prefetch inteligente:
   * En desktop, si el usuario pasa el mouse por la lupa, precargamos.
   * No afecta carga inicial y mejora la sensación al abrir.
   */
  document.addEventListener(
    'pointerenter',
    function (event) {
      const trigger = event.target.closest && event.target.closest('[data-dl-search-open]');
      if (!trigger) return;

      ensureModalLoaded().catch(function (error) {
        log(error);
      });
    },
    true
  );

  document.addEventListener(
    'focusin',
    function (event) {
      const trigger = event.target.closest && event.target.closest('[data-dl-search-open]');
      if (!trigger) return;

      ensureModalLoaded().catch(function (error) {
        log(error);
      });
    },
    true
  );

  window.openSearchModal = openSearchModal;
  window.closeSearchModal = closeSearchModal;
  window.submitQuickAdd = submitQuickAdd;

  window.DecantsSearchLoader = {
    __initialized: true,
    open: openSearchModal,
    close: closeSearchModal,
    preload: ensureModalLoaded,
    search: performSearch,
  };
})();